import React, { useMemo } from 'react'
import clxs from 'clsx'
import { Box, Typography, Link } from '@material-ui/core'
import usePagination from '@material-ui/core/usePagination'
import { makeStyles } from '@material-ui/styles'
import { mutualTheme } from '~/utils/theme'
import { SecondaryButton } from '~/components'
import { ThreeDotsIcon } from '~/assets/icons'

const useStyles = makeStyles({
  container: {
    paddingTop: mutualTheme.spacing(1),
    paddingBottom: mutualTheme.spacing(1)
  },
  link: {
    paddingRight: mutualTheme.spacing(1),
    paddingLeft: mutualTheme.spacing(1),
    marginLeft: mutualTheme.spacing(1),
    marginRight: mutualTheme.spacing(1),
    fontSize: 16
  },
  pageLink: {
    cursor: 'pointer',
    color: mutualTheme.palette.secondary.main
  },
  disabledLink: {
    pointerEvents: 'none',
    cursor: 'default',
    color: mutualTheme.palette.text.disabled
  },
  nextButton: {
    marginLeft: mutualTheme.spacing(1)
  },
  previousButton: {
    marginRight: mutualTheme.spacing(1)
  }
})

export type PagerProps = {
  page: number,
  onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void,
  pageCount: number,
  disabled?: boolean,
  caption?: {
    currentNumberOfItems: number,
    matchCount: number,
    sufix: string
  }
}

export const TablePager: React.FC<PagerProps> = (props) => {
  const {
    onPageChange,
    pageCount,
    caption,
    page
  } = props
  const { items } = usePagination({
    count: pageCount,
    onChange: onPageChange,
    page,
    siblingCount: 0
  })
  const classes = useStyles()

  const paginationActions: JSX.Element[] = useMemo(() => {
    const actions = items.map(({ page, type, selected, onClick, disabled }, index) => {
      if (type === 'end-ellipsis' || type === 'start-ellipsis') {
        return <ThreeDotsIcon key={index} />
      }

      if (type === 'page') {
        return (
          <Link
            key={`page-${page}`}
            className={clxs(classes.link, {
              [classes.pageLink]: !selected,
              [classes.disabledLink]: selected || props.disabled
            })}
            onClick={onClick}
          >
            {page}
          </Link>
        )
      }

      return (
        <SecondaryButton
          key={`pager-buttons-${page}`}
          className={type === 'previous' ? classes.previousButton : classes.nextButton}
          color='secondary'
          size='small'
          onClick={onClick}
          disabled={props.disabled || disabled}
        >
          {type === 'next' ? 'Próximo' : 'Anterior'}
        </SecondaryButton>
      )
    })

    return actions
  }, [classes.disabledLink, classes.link, classes.nextButton, classes.pageLink, classes.previousButton, items, props.disabled])

  return (
    <Box className={classes.container} sx={{ display: 'flex', alignItems: 'center' }}>
      {caption && (
        <Box sx={{ flex: 1 / 3 }}>
          <Typography variant='caption'>
            Exibindo {caption.currentNumberOfItems} de {caption.matchCount} {caption.sufix}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          flex: 1 / 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {paginationActions}
        </Box>
      </Box>
    </Box>
  )
}
